import Loader from '@/common/helper/loader';
import { queryString } from '@/common/dependencies';
import { HelperCore } from "@/common/helper";

import {
  createStorage,
  isObject,
  isStringEmpty,
  nanoid,
  throttle,
} from '../utils';
import navbarStore from '@/store/navbar';
import chatStoreManager from '@/store/chat/manager';
import client, { SIZE_MODEL_SMALL } from '@/store/client';
import wsStore from '@/components/chat-ws/store';

const MD_URL_RE = /(\[)?(\[([^\]]*)\]\((.*?)\))(\])?/gm;

export const COMMON_MESSAGE = '当前使用人数过多，请稍后再试';
export const UNKNOW_MESSASGE = '未知消息，请稍后重试。';
export const UNKNOW_EXCEPTION_MESSASGE = '未知异常，请稍后重试。';
export const TIMEOUT_MESSASGE = '请求超时，请重试。';
export const NETWORK_ERROR_MESSAGE = '网络异常，请检查网络。';
export const SERVICE_ERROR_MESSAGE = '服务异常，请稍后重试。';
export const EMPTY_MESSAGE = '当前使用人数过多，请稍后再试。';
export const ABORT_MESSAGE = '`已停止回答`';
export const CHAT_ERROR_MESSAGE = {
  424: '问题长度超限，如果您使用了“联网搜索”功能，请关闭“联网搜索”之后重试。',
}
const SENSITIVE_MESSAGE =
  '作为一个人工智能语言模型，您这个问题我暂时无法回答，但您可以问一些其他问题，我会努力帮您解决';
export const RESPONSE_ERROR_MESSAGE = {
  4007: SENSITIVE_MESSAGE,
  40042: SENSITIVE_MESSAGE,
  40043: '文件解析失败，请稍后再试',
  40055: '抱歉，您的询问过于频繁，请稍后再试。',
  40056: '抱歉，您的询问过于频繁，请明天再试。',
  40057: '当前对话已达上限～继续提问请新建话题。',
  40096: '问题为空',
  41000: '该数字人已不存在',
  41001: COMMON_MESSAGE,
  41002: COMMON_MESSAGE,
  41003: COMMON_MESSAGE,
  50001: COMMON_MESSAGE, // 发布会，临时
  // 50001: '哎呀，服务器开小差了，请重试～',
  110008: `您因为违反360助手平台用户协议，已被封禁。`,
  501: SENSITIVE_MESSAGE,
  100008: '请求参数格式错误',
  110005: '未授权的请求',
  300100: '该对话已删除',
};
export const AUDIT_ERROR_CODE = 40042;

function newMessageId() {
  return `msg-${nanoid()}`;
}

export const checkFEMessageId = id => /^msg-/.test(id);

export const MessageType = {
  Normal: 'normal',
  Welcome: 'welcome',
  ClearContext: 'cut',
};

export const createChatMessage = ({
  asstId,
  chatId,
  messageId,
  input,
  page,
  ...reset
}) => {
  const id = messageId || newMessageId();
  return {
    msg_id: id,
    virtual_message_id: id,
    conversation_id: chatId,
    role_id: asstId,
    prompt: input,
    is_doing: 1, // 1-对话过程中 0-对话结束
    is_end: null, // 1-回答内容完整 0-不完整
    loading: true,
    typing: false,
    result: '', // 回答
    page,
    ...reset,
  };
};

export const createWelcomeMessage = (roleId, text) => {
  const id = newMessageId();
  return {
    msg_id: id,
    msg_type: MessageType.Welcome,
    role_id: roleId,
    is_doing: 0,
    is_end: 1,
    result: text,
  };
};

export const createResumeMessage = async message => {
  const { msg_id, role_id, prompt, msg_group, rewrite_type } = message;

  const {
    last_event_id = 0,
    conversation_id,
    result = '',
    search_keyword = [],
    search_total = 0,
    refer_search = [],
    role_group,
    startDate,
    responseDate,
    file,
    page,
    txt,
    annex_msg_id,
    debate,
    dataset,
    dataset_src,
    dataset_info,
    reasoning_text,
    reasoning_duration,
    user_knowledge,
    events,
    code_info,
    from_aiso,
  } = await getChatStorageItem(msg_id);

  const getArray = value => (Array.isArray(value) ? value : []);
  const loading = isStringEmpty(result) && !events?.length;
  const data = {
    retry: true,
    last_event_id,
    chatId: conversation_id,
    asstId: role_id,
    messageId: msg_id,
    loading,
    typing: !loading,
    input: prompt,
    search_keyword: getArray(search_keyword),
    search_total,
    refer_search: getArray(refer_search),
    result,
    msg_group,
    role_group,
    startDate,
    rewrite_type,
    responseDate,
    file,
    page,
    txt,
    annex_msg_id,
    debate,
    dataset,
    dataset_src,
    dataset_info,
    reasoning_text,
    reasoning_duration,
    user_knowledge,
    events,
    code_info,
    from_aiso,
    ...getFixedRenewData(message),
  };

  HelperCore.log(`createResumeMessage`, data);

  return data;
};

const STORAGE_CHAT_KEY = 'chat-storage';

const chatStorage = createStorage(
  STORAGE_CHAT_KEY,
  {},
  {
    serialization: {
      serialize: v => JSON.stringify(v),
      deserialize: v => {
        let value = {};
        try {
          if (!isStringEmpty(v)) {
            value = JSON.parse(v);
          }
        } catch {}
        return value;
      },
    },
  }
);

export const persistMessage = throttle(
  async message => {
    const {
      last_event_id,
      conversation_id,
      msg_id,
      result,
      search_keyword,
      search_total,
      refer_search,
      page,
      role_group,
      startDate,
      responseDate,
      file,
      txt,
      annex_msg_id,
      kwargs,
      dataset,
      dataset_src,
      dataset_info,
      debate,
      reasoning_text,
      reasoning_duration,
      user_knowledge,
      events,
      code_info,
      from_aiso,
    } = message || {};
    if (last_event_id >= 1) {
      const data = await chatStorage.get();
      const date = new Date();
      const expires = new Date(date.setDate(date.getDate() + 1)).getTime();
      data[msg_id] = {
        value: {
          last_event_id,
          conversation_id,
          msg_id,
          result,
          search_keyword,
          search_total,
          refer_search,
          page,
          role_group,
          startDate,
          responseDate,
          file,
          txt,
          annex_msg_id,
          kwargs,
          dataset,
          dataset_src,
          dataset_info,
          debate,
          reasoning_text,
          reasoning_duration,
          user_knowledge,
          events,
          code_info,
          from_aiso,
        },
        expires,
      };
      chatStorage.set(data);
    }
  },
  2000,
  { leading: true, trailing: false }
);

export async function getChatStorageItem(id) {
  const data = await chatStorage.get();
  const item = data[id];
  if (isObject(item, true)) {
    const { value, expires } = item;
    if (!checkExpires(expires)) {
      return value;
    }
  }
  return {};
}

export async function removeChatStorageItem(id) {
  const data = await chatStorage.get();
  if (data[id]) {
    delete data[id];
    chatStorage.set(data);
  }
}

export function clearChatStorage() {
  chatStorage.set({});
}

function checkExpires(ts) {
  return Number.isNaN(Number(ts)) || ts < Date.now();
}

async function checkSearchCacheExpires() {
  const data = await chatStorage.get();

  if (isObject(data, true)) {
    Object.keys(data).forEach(key => {
      if (checkExpires(data[key]?.expires)) {
        delete data[key];
      }
    });
    chatStorage.set(data);
  }
}

checkSearchCacheExpires();

/**
 * 模型分类展示名
 */
export const getModelIntentionResult = (message) => {
  const fcResult = message?.stat_model?.fc_result;
  if(fcResult){
    try{
      const fc_result = JSON.parse(fcResult);
      if(fc_result.title){
        return fc_result;
      }
    } catch(e){}
  }

  const intentionResult = message?.stat_model?.intention_result;
  if(!intentionResult){
    return {};
  }
  try{
    const intention_result = JSON.parse(intentionResult);
    return intention_result;
  } catch(e){}
  return {};
}
export const getModelCategory = (message) => {
  const modelIntentionResult = getModelIntentionResult(message);
  return modelIntentionResult.title || "";
}
export const getModelName = (message) => {
  const modelName = message?.stat_model?.streaming_model_name;
  if(!modelName){
    return null;
  }
  try{
    return JSON.parse(modelName);
  }catch(e){}
  return null;
}

/**
 * 参考资料容错
 */
export const getFixedReferSearch = (list, prompt) => {
  return (list || []).map(item => ({ ...item, ...{ title : item.title || (prompt || '') } }));
}

/**
 * 视频亮点格式化
 */
export const getFixedVideoWatch = (video_watch) => {
  if(!video_watch?.length){
    return [];
  }

  let res = null;
  video_watch.forEach((item, index) => {
    if(index == 0){
      res = item;
    }
    else if(res) {
      res.parse_result = (res.parse_result || []).concat(item.parse_result || []);
    }
  });
  if(!res?.parse_result?.length){
    return [];
  }
  return [res];
}

/**
 * 受限的消息，只显示正文，参考
 * 不显示思维导图、相关视频等
 * @param {Message} message 消息
 */
export const isLimitedMessage = (message) => {
  // 1. src^=360se && 寻址
  // TODO 待具体需求
  // const urlParams = Helper.getParams();
  // if(urlParams.src && /^360se/.test(urlParams.src) && message?.stat == "website"){
  //   return true;
  // }

  if(!message){
    return false;
  }

  // Agent
  if(message.msg_type == "flow"){
    return false;
  }

  // 风控
  if(message.is_ban_ext == 1){
    return true;
  }

  return false;
}
/**
 * 只读会话
 * web 隐藏项目:
 * 改写，翻译，导出，脑图扩展，追问提问，收藏，点赞，反馈
 * h5 隐藏项目:
 * 反馈，收藏
 * @param {Conversation} conversation 会话
 */
export const isReadOnlyConversation = (conversation) => {
  // 只读
  if(conversation?.ban_edit == 1){
    return true;
  }

  return false;
}

/**
 * 参考资料 = 参考资料 + 论文资料
 * @param {Message} message 消息
 */
export const getReferSearchCount = (message) => {
  if(!message){
    return 0;
  }
  return (message.refer_search?.length ?? 0) + (message.paper_result?.list?.length ?? 0)
}
export const getReferSearchList = (message) => {
  if(!message){
    return [];
  }
  const list = (message.paper_result?.list || []).map(item => {
    return {
      url: item.url,
      title: item.title || item.zh_title,
      // site: Helper.substring(item.source, 30, '...'),
      site: "",
      icon: null,
      // date: Helper.substring(item.published, 20, '...'),
      date: "",
      summary: item.desc,
    }
  }).concat(message.refer_search || []);
  return list;
}

export const isRefererLink = (link) => {
  if(!link){
    return false;
  }
  const {
    href,
    content,
  } = link;

  if(href === "URL"){
    return true;
  }

  return false;
}

export const getRefererData = (link, referenceSites) => {
  if(!link){
    return false;
  }
  const {
    href,
    content,
  } = link;

  const contentOk = content && /^\d+$/.test(content);
  const isHrefOk = (href === "") || (href && (href === "URL" || /^http/.test(href)));

  // 非引用，不干预
  if(!contentOk){
    return;
  }

  // 无效引用，不显示
  if(!isHrefOk){
    return false;
  }

  // href 为无效链接，取 referenceSites
  if(!referenceSites?.length){
    return false;
  }
  const item = referenceSites[content - 1];
  if(!item){
    return false;
  }
  return {
    ...item,
    ...{
      url : /^http/.test(href) ? href : item.url,
    },
  };
}

/**
 * result 格式化后交给 Markdown 组件渲染
 *    1. 处理引用
 * @param {String} result 正文
 * @param {?message} messageItem  消息
 * @param {Boolean} noRefer 过滤引用
 */
export const getFormattedResult = (result, { refer_search, user_knowledge } = {}, noRefer) => {
  if (isStringEmpty(result)) {
    return result;
  }

  const reference = (refer_search || []).concat(user_knowledge || []);

  let text = result;
  if (noRefer || (reference.length > 0)) {
    text = text.replace(MD_URL_RE, (...args) => {
      // 不支持引用
      if(noRefer){
        if(/^\[\[/.test(args[0] || "")){
          return "";
        }
      }

      // 支持引用
      const [replaceValue, name, dummyUrl] = args.slice(2, 5);
      // 正文中引用不再使用 www.数字.com 格式，而且会出现其他链接
      // 不再考虑隐藏链接输出过程
      return replaceValue;
    });
  }
  return Loader.splitUrlInMarkdown(text);
}

/**
 * 再试一次、断点续传时，取原消息的相关信息
 * @param {Message} message
 * @returns {Object} page
 */
export const getFixedRenewData = (message) => {
  const {
    page,
    txt,
    role_biz,
    kwargs,
    file,
    translate_type,
    mind_map,
    rewrite_type,
    dataset,
    dataset_info,
  } = message;
  const data = {
    txt,
    file,
    role_biz,
    rewrite_type,
    kwargs,
    translate_type,
    dataset,
    dataset_info,
    payload : null, // 视频消息需要的 payload，无法由历史消息拼接，由服务端处理
  };
  try{
    const pages = typeof page == "string" ? JSON.parse(page) : page;
    if(pages?.length){
      Object.assign(data, {
        page : pages,
        summary : role_biz == "summary_page",
      });
    }
    if(role_biz == "mind" || role_biz == "page_mind") {
      Object.assign(data, {
        kwargs: {
          sub_process: [
            "mind"
          ]
        }
      })
    }
  } catch(e){}
  return data;
}

/**
 * 会话报错，打点参数
 */
export const getFailMessageParams = (message) => {
  if(!message){
    return {};
  }

  const { prompt, status, openCode, result } = message;
  const params = {
    fail_reason : "sys_exception",               // 错误类型 illegal/sys_exception/empty
    fail_code : "",                            // 错误码
    ext3 : prompt || "",                  // prompt
  }

  if(openCode){
    if(openCode == AUDIT_ERROR_CODE){
      params.fail_reason = "illegal";
    }
    params.fail_code = openCode;
  }
  else {
    if(status == -1){
      params.fail_reason = "illegal";
      params.fail_code = AUDIT_ERROR_CODE;
    }
    else if(!result){
      params.fail_reason = "empty";
    }
  }

  return params;
}

export const createClearContextMessage = chatId => {
  const id = newMessageId();
  return {
    msg_id: id,
    msg_type: 'cut',
    conversation_id: chatId,
    is_doing: 0,
    is_end: 1,
  };
};

// 附件消息父 ID
export const getAnnexMsgId = (message) => {
  if(!message){
    return "";
  }
  if(message.annex_msg_id){
    return message.annex_msg_id;
  }
  if(message.role_biz && [
    "doc",
    "chat_image",
    "extract_image",
    "explain_image",
    "page",
    "summary_video",
  ].includes(message.role_biz)){
    return message.msg_id;
  }
  return "";
}

export const getKwargs = (kwargs) => {
  const isWeiShiMinWindow = client.isClient && client.sizeModel == SIZE_MODEL_SMALL;
  const store = isWeiShiMinWindow
  ? wsStore.currentStore || chatStoreManager.current
  : chatStoreManager.current;
  const activeModel = navbarStore.chatModels[store.currAsstId];
  const customScene = activeModel?.chat_type === 100 ? {scene: 'custom'} : {}
  const membership = { membership: client.is360se || (HelperCore.isMac() &&  client.isLLQ)? false : true }
  return {
    ...queryString.parse(window.location.search),
    ...kwargs,
    ...customScene,
    ...membership
  }
}

export const generateCustomAPIParams = (file) => {
  const isWeiShiMinWindow = client.isClient && client.sizeModel == SIZE_MODEL_SMALL;
  const store = isWeiShiMinWindow
  ? wsStore.currentStore || chatStoreManager.current
  : chatStoreManager.current;
  const activeModel = navbarStore.chatModels[store.currAsstId];

  const customModelField = activeModel?.chat_type === 100 ? {
    role: activeModel.id,
    firm_id: activeModel.firm_id,
    role_biz: file && file.length ? 'doc' : 'common'
  } : {}

  return customModelField;
}


// 日志上报
export const getMessageLogData = (message, needString = true) => {
  if(!message){
    return {};
  }

  const res = { ...message };
  if(message.page?.length){
    const page = HelperCore.copyObj(message.page[0]);
    page.content = HelperCore.substring(page.content, 100, '');
    res.page = [page];
  }

  if(needString){
    return JSON.stringify(res);
  }
  return res;
}

export const getResultForRead = (markdown) => {
  if(!markdown){
    return "";
  }

  // 移除代码块
  let filtered = markdown.replace(/```[\s\S]*?```|~~~[\s\S]*?~~~/g, '');
  filtered = filtered.replace(/ {4,}[^\n]*\n/g, '');

  // 移除表格
  filtered = filtered.replace(/^\|.+\|.+\n\|[-: ]+[-|: ]+\n(\|.+\|.+\n)*/gm, '');

  return filtered;
}

/**
 * 再次发送消息时，取上一条消息的部分数据
 */
export const fixBodyByLastMessage = (body, lastMessage) => {
  if(!body || !lastMessage){
    return;
  }

  // AI 搜索
  if(lastMessage.from_aiso && !body.from_aiso){
    body.from_aiso = lastMessage.from_aiso;
  }
}
